<template>
  <v-select
    v-model="internalValue"
    :options="options"
    :reduce="boundReduce"
    :label="labelKey"
    :clearable="clearable"
    :disabled="disabled"
    :placeholder="placeholder"
    class="image-text-select"
  >
    <template #selected-option="option">
      <div class="d-flex align-items-center image-text-select-option">
        <img
          v-if="shouldShowImage(option)"
          :src="pictureUrl(option)"
          alt=""
          class="image-text-select-thumb mr-50"
        >
        <span>{{ optionLabel(option) }}</span>
      </div>
    </template>
    <template #option="option">
      <div class="d-flex align-items-center image-text-select-option">
        <img
          v-if="shouldShowImage(option)"
          :src="pictureUrl(option)"
          alt=""
          class="image-text-select-thumb mr-50"
        >
        <span>{{ optionLabel(option) }}</span>
      </div>
    </template>
  </v-select>
</template>

<script>
import vSelect from 'vue-select'
import { resolvePictureUrl } from '@/libs/pictureUrl'
import { $themeConfig } from '@themeConfig'

export default {
  name: 'ImageTextSelect',
  components: {
    'v-select': vSelect,
  },
  props: {
    modelValue: {
      type: [String, Number],
      default: undefined,
    },
    value: {
      type: [String, Number],
      default: undefined,
    },
    options: {
      type: Array,
      default: () => [],
    },
    labelKey: {
      type: String,
      default: 'name',
    },
    valueKey: {
      type: String,
      default: 'id',
    },
    imageKey: {
      type: String,
      default: 'picture',
    },
    fallbackImage: {
      type: String,
      default: () => $themeConfig.app.noUnitImage || '/static_images/no_image.png',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'input'],
  computed: {
    internalValue: {
      get() {
        if (this.modelValue !== undefined) {
          return this.modelValue
        }
        return this.value
      },
      set(val) {
        this.$emit('update:modelValue', val)
        this.$emit('input', val)
      },
    },
    boundReduce() {
      return (option) => option?.[this.valueKey]
    },
  },
  methods: {
    optionLabel(option) {
      return option?.[this.labelKey] ?? ''
    },
    shouldShowImage(option) {
      const id = option?.[this.valueKey]
      return id != null && id !== ''
    },
    pictureUrl(option) {
      return resolvePictureUrl(option?.[this.imageKey], this.fallbackImage)
    },
  },
}
</script>

<style scoped>
.image-text-select {
  min-width: 0;
  flex-shrink: 0;
}

.image-text-select-option {
  min-height: 1.5rem;
}

.image-text-select-thumb {
  width: 24px;
  height: 24px;
  object-fit: cover;
  border: 1px solid var(--image-text-select-thumb-border, #ddd);
  border-radius: 3px;
  flex-shrink: 0;
}
</style>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/include';

body.dark-layout {
  .image-text-select {
    --image-text-select-thumb-border: #{$theme-dark-border-color};
    --vs-border-color: #{$theme-dark-input-border-color};
    --vs-dropdown-bg: #{$theme-dark-input-bg};
    --vs-dropdown-color: #{$theme-dark-body-color};
    --vs-search-input-color: #{$theme-dark-body-color};
    --vs-search-input-placeholder-color: #{$theme-dark-input-placeholder-color};
    --vs-selected-bg: transparent;
    --vs-selected-color: #{$theme-dark-body-color};
    --vs-controls-color: #{$theme-dark-body-color};
    --vs-dropdown-option-color: #{$theme-dark-body-color};
    --vs-dropdown-option--active-bg: #{rgba($primary, 0.15)};
    --vs-dropdown-option--active-color: #{$theme-dark-body-color};
    --vs-state-disabled-bg: #{$theme-dark-input-disabled-bg};
    --vs-state-disabled-color: #{$theme-dark-text-muted-color};
    --vs-colors--darkest: rgba(0, 0, 0, 0.35);

    .vs__dropdown-toggle {
      background-color: $theme-dark-input-bg;
    }

    .vs__dropdown-menu {
      border-color: $theme-dark-input-border-color;
      box-shadow: $theme-dark-box-shadow;
    }

    .vs__selected,
    &.vs--single .vs__selected {
      color: $theme-dark-body-color !important;
    }

    .vs__dropdown-option--highlight {
      color: $theme-dark-body-color !important;
    }

    .vs__clear svg,
    .vs__open-indicator {
      fill: $theme-dark-body-color;
    }
  }
}
</style>
